import { useEffect, useState } from "react";
import { Check, CircleDashed, Users, BadgeCheck, Banknote, GraduationCap } from "lucide-react";
import { api, euro, formatApiError } from "@/lib/api";
import { Reveal, SectionLabel } from "@/components/Reveal";

function SessionEditor({ f, onSaved }) {
  const sess = f.sessions?.[0] || {};
  const [periode, setPeriode] = useState(sess.periode || "");
  const [lieu, setLieu] = useState(sess.lieu || "");
  const [places, setPlaces] = useState(sess.places ?? 6);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/update-session", {
        slug: f.slug, periode, lieu, places: Number(places) || 0,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved();
    } catch (e) {
      console.error(formatApiError(e));
    }
    setSaving(false);
  };

  const inputCls =
    "w-full rounded-lg border border-aqua/20 bg-abyss/70 px-3 py-2 text-xs text-foam placeholder:text-foam/30 outline-none transition-all duration-300 focus:border-aqua";

  return (
    <div className="glass-card rounded-2xl p-5" data-testid={`session-editor-${f.slug}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-aqua">
          Module {f.module_code} · {euro(f.price)}
        </p>
        {saved && (
          <span className="font-mono text-[10px] text-aqua" data-testid={`session-saved-${f.slug}`}>
            Enregistré ✓
          </span>
        )}
      </div>
      <p className="mt-1.5 font-serif text-lg text-foam">{f.title}</p>
      <div className="mt-3 space-y-2.5">
        <input
          value={periode}
          onChange={(e) => setPeriode(e.target.value)}
          placeholder="Dates — ex. 14 au 18 avril 2026"
          data-testid={`session-periode-${f.slug}`}
          className={inputCls}
        />
        <input
          value={lieu}
          onChange={(e) => setLieu(e.target.value)}
          placeholder="Lieu — ex. Près de La Rochelle"
          data-testid={`session-lieu-${f.slug}`}
          className={inputCls}
        />
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={50}
            value={places}
            onChange={(e) => setPlaces(e.target.value)}
            placeholder="Places"
            data-testid={`session-places-${f.slug}`}
            className="w-24 rounded-lg border border-aqua/20 bg-abyss/70 px-3 py-2 text-xs text-foam outline-none focus:border-aqua"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid={`session-save-${f.slug}`}
            className="flex-1 rounded-full bg-aqua py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-abyss transition-all duration-300 hover:bg-foam disabled:opacity-40"
          >
            {saving ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Ecole() {
  const [data, setData] = useState(null); // null = loading, "error" = denied
  const [busy, setBusy] = useState("");

  const load = () =>
    api
      .get("/admin/overview")
      .then((r) => setData(r.data))
      .catch(() => setData("error"));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (userId, slug) => {
    setBusy(`${userId}-${slug}`);
    try {
      await api.post("/admin/toggle-module", { user_id: userId, formation_slug: slug });
      await load();
    } catch (e) {
      console.error(formatApiError(e));
    }
    setBusy("");
  };

  if (data === null) {
    return (
      <main className="min-h-screen grid place-items-center bg-abyss pt-[72px]" data-testid="ecole-loading">
        <div className="h-10 w-10 rounded-full border-2 border-aqua/30 border-t-aqua animate-spin" />
      </main>
    );
  }

  if (data === "error") {
    return (
      <main className="min-h-screen grid place-items-center bg-abyss pt-[72px]" data-testid="ecole-denied">
        <p className="text-sm text-foam/60">Accès réservé à l'école.</p>
      </main>
    );
  }

  const { stats, formations, members } = data;

  const statCard = (Icon, v, l, tid) => (
    <div className="glass-card rounded-2xl p-6" data-testid={tid}>
      <Icon size={20} className="text-aqua" />
      <p className="mt-4 font-serif text-3xl md:text-4xl text-foam">{v}</p>
      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foam/50">{l}</p>
    </div>
  );

  const fmtDate = (s) =>
    s
      ? new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
      : "";

  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="ecole-page">
      <div className="mx-auto max-w-7xl px-5 pt-14 md:px-10">
        <Reveal>
          <SectionLabel>Espace école — OJANZU</SectionLabel>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl font-light text-foam" data-testid="ecole-title">
            Suivi des <em className="italic text-aqua">stagiaires</em>.
          </h1>
          <p className="mt-3 text-sm text-foam/55">
            Inscriptions, paiements encaissés et validation des modules du cursus praticien.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {statCard(Users, `${stats.members}`, "Membres inscrits", "admin-stat-members")}
          {statCard(BadgeCheck, `${stats.confirmed_enrollments}`, "Inscriptions confirmées", "admin-stat-enrollments")}
          {statCard(Banknote, euro(stats.revenue_cents / 100), "Revenus encaissés", "admin-stat-revenue")}
          {statCard(GraduationCap, `${stats.modules_validated}`, "Modules validés", "admin-stat-modules")}
        </div>

        <Reveal delay={0.02}>
          <h2 className="mt-14 font-serif text-2xl text-foam">Sessions &amp; dates</h2>
          <p className="mt-2 text-sm text-foam/55">
            Renseignez les dates définitives de chaque module — elles s'affichent aussitôt sur
            le site, à la place de « dates modulables ».
          </p>
          <div className="mt-7 grid gap-4 sm:grid-cols-2" data-testid="session-editor">
            {formations.map((f) => (
              <SessionEditor key={f.slug} f={f} onSaved={load} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-16 font-serif text-2xl text-foam">Stagiaires</h2>
          <ul className="mt-7 space-y-5" data-testid="admin-members-list">
            {members.map((m) => (
              <li key={m.id} data-testid={`member-card-${m.email}`} className="glass-card rounded-2xl p-6">
                <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4">
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-aqua/15 font-serif text-lg text-aqua">
                      {m.name?.[0]}
                    </span>
                    <div>
                      <p className="text-base font-semibold text-foam">
                        {m.name}
                        {m.role === "admin" && (
                          <span className="ml-3 rounded-full border border-aqua/40 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-aqua">
                            école
                          </span>
                        )}
                      </p>
                      <p className="font-mono text-[11px] text-foam/45">{m.email}</p>
                    </div>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-foam/40">
                    {m.created_at ? `Inscrit·e le ${fmtDate(m.created_at)}` : ""}
                  </p>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {formations.map((f) => {
                    const validated = m.completed_modules.includes(f.lookup_key);
                    const booked = m.enrollments.some(
                      (e) => e.lookup_key === f.lookup_key && e.status === "confirmed"
                    );
                    return (
                      <div
                        key={f.lookup_key}
                        className={`rounded-xl border p-4 ${
                          validated
                            ? "border-aqua/50 bg-aqua/10"
                            : booked
                              ? "border-aqua/25 bg-deep/40"
                              : "border-aqua/10 bg-deep/20"
                        }`}
                        data-testid={`member-module-${m.id}-${f.lookup_key}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-aqua/80">
                            {f.module_code}
                          </p>
                          {validated ? (
                            <Check size={14} className="text-aqua" />
                          ) : booked ? (
                            <CircleDashed size={14} className="text-foam/40" />
                          ) : null}
                        </div>
                        <p className="mt-1.5 text-xs font-semibold leading-snug text-foam/90">{f.title}</p>
                        <p className="mt-1 text-[10px] text-foam/45">
                          {validated ? "Validé" : booked ? "Session réservée" : "Non réservé"} · {euro(f.price)}
                        </p>
                        <button
                          data-testid={`admin-module-toggle-${m.id}-${f.lookup_key}`}
                          onClick={() => toggle(m.id, f.lookup_key)}
                          disabled={busy === `${m.id}-${f.lookup_key}`}
                          className={`mt-3 w-full rounded-full py-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 disabled:opacity-40 ${
                            validated
                              ? "border border-aqua/40 text-aqua hover:bg-aqua hover:text-abyss"
                              : "bg-aqua text-abyss hover:bg-foam"
                          }`}
                        >
                          {busy === `${m.id}-${f.lookup_key}` ? "…" : validated ? "Retirer la validation" : "Valider le module"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </main>
  );
}
